import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Building2, Check, CheckCircle2, ChevronDown, Circle, Clock, Edit2, Factory, FileSpreadsheet, Lightbulb, Loader2, LogOut, MapPin, Plus, Trash2, X } from "lucide-react";
import { API, formatApiError } from "../auth/api";
import AddressField from "../components/AddressField";
import CalendarsCopy from "../components/CalendarsCopy";
import ImportDialog from "../components/ImportDialog";
import SuggestInput from "../components/SuggestInput";
import WeeklyHours, { weekFromRange, weekSummary } from "../components/WeeklyHours";
import { HoursSection, HolidaySection } from "./LocationAdminPage";
import FixedSchedulesTab from "./FixedSchedulesTab";

/** 2026-10-28 (M11): Faste tømninger på en plads, set fra logistiksiden - fold ud pr. plads. */
export function SiteFixed({ site, canEdit = true, readOnly = false, testid }) {
  const [open, setOpen] = useState(false);
  if (!site?.location_code) return null;
  return (
    <div className="mt-2" data-testid={testid || `site-fixed-${site.location_code}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700">
        Faste tømninger <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3">
        <FixedSchedulesTab locations={[{ code: site.location_code, name: site.name }]} embedded readOnly={readOnly || !canEdit} />
      </div>}
    </div>
  );
}

/**
 * Modtageanlæg og egne kunder med pladser (2026-10-26/27).
 *
 * - Modtageanlæg: firmaets ÉN liste over leveringsadresser (forbrændings- og
 *   sorteringsanlæg ...). Bruges på egne ture og hos egne kunder med pladser.
 *   Navnet + "andre navne" er det, Pickup og mails genkender - der gættes aldrig.
 * - Egne kunder med pladser: kunder, der ikke selv bruger ContainerFlow, men har
 *   genbrugspladser med containere, I kører for (fx ARGO med Pickup). Samme
 *   opbygning som en kunde i ContainerFlow - pladser, materialer, containere og
 *   åbnings- og helligdagskalendere pr. materiale (primær leveringsadresse og
 *   alternativer med egne tider) - men I vedligeholder den. Data ligger hos
 *   kunden, så den kan overtage det hele, hvis den selv kommer på ContainerFlow.
 */
const card = "bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm";
const input = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const label = "block text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1";
const btnPrimary = "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-600 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50";
const amber = "px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
const norm = (s) => (s || "").toLowerCase().replace(/\b(a\/s|aps|i\/s)\b/g, " ").replace(/[^a-z0-9æøå]+/g, " ").trim();
const hours = (p) => (p.open_from && p.open_to ? `${p.open_from}–${p.open_to}` : "");

/**
 * 2026-10-27: Opret en egen kunde med pladser. Findes kunden allerede som egen kunde uden
 * pladser, spørges der: så bliver den samme kunde en kunde med pladser, og prisaftalerne og
 * turene flyttes med fra i dag (ellers to sæt priser for samme kunde).
 */
/** Hvad der bliver kundens, hvis kunden selv kommer på ContainerFlow - og hvad der altid er jeres. */
export function OwnershipNote() {
  return (
    <div className="text-[11px] rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-2.5 py-2 text-slate-600 dark:text-slate-300" data-testid="ext-ownership">
      <b>Kommer kunden selv på ContainerFlow</b> (ContainerFlow overdrager efter at have tjekket, at det er kunden), bliver kundens egne
      oplysninger kundens: pladser, containere, leveringsadresser og -kalendere og bestillinger i kundens navn (med de ture, der er kørt for dem) -
      ligesom hos alle kunder i ContainerFlow. I kører videre som vognmand. <b>Jeres forbliver jeres:</b> priser, listen over modtageanlæg,
      egne lokationer og egne kunder, interne noter og chauffører.
    </div>
  );
}

/**
 * 2026-10-27: Kunder med pladser og deres pladser oprettes af ContainerFlow (så hver kun findes
 * én gang, også når flere vognmænd kører for kunden). Logistikfirmaet sender en anmodning.
 * body: { kind: "customer" | "site", customer_name | customer_id, site_name, address, city, note }
 */
export async function requestExternal(body) {
  return (await axios.post(`${API}/logistics/master-requests`, body)).data;
}

/** 2026-10-28: En liste over pladser kopieret fra Excel (faneblad) eller skrevet med semikolon:
 *  navn; vej og nr.; postnr. og by (postnr. og by må gerne stå i hver sin kolonne). */
export function parseSites(text) {
  const lines = (text || "").split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const rows = lines.map((l) => {
    const parts = (l.includes("\t") ? l.split("\t") : l.split(";")).map((x) => x.trim());
    return { name: parts[0] || "", address: parts[1] || "", city: parts.slice(2).filter(Boolean).join(" ") };
  });
  if (rows.length && /^(navn|plads|pladsens navn|name|lokation)$/i.test(rows[0].name)) rows.shift();
  return rows;
}

const ST_CHIP = { ok: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  exists: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200", error: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200" };
const ST_TXT = { ok: "Ny", exists: "Findes", error: "Ret" };

/** Kontrollen af listen - samme som ContainerFlow laver igen, før pladserne oprettes. */
export function SiteRowsTable({ rows, testid = "req-rows" }) {
  if (!rows?.length) return null;
  return (
    <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700" data-testid={testid}>
      <table className="w-full text-xs">
        <thead className="bg-slate-50 dark:bg-slate-900 text-slate-500 sticky top-0"><tr>
          <th className="text-left px-2 py-1.5 w-8">#</th><th className="text-left px-2 py-1.5">Plads</th><th className="text-left px-2 py-1.5">Adresse</th><th className="text-left px-2 py-1.5">Kontrol</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-100 dark:border-slate-700 align-top" data-testid={`${testid}-${i}`}>
            <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
            <td className="px-2 py-1.5 font-semibold">{r.name || <span className="text-slate-400">-</span>}</td>
            <td className="px-2 py-1.5 text-slate-600 dark:text-slate-300">{[r.address, r.city].filter(Boolean).join(", ") || <span className="text-slate-400">-</span>}</td>
            <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${ST_CHIP[r.status] || ""}`}>{ST_TXT[r.status] || r.status}</span>
              {[...(r.errors || []), ...(r.warnings || [])].map((m) => <div key={m} className={`mt-0.5 ${(r.errors || []).includes(m) ? "text-red-700 dark:text-red-300" : "text-slate-500"}`}>{m}</div>)}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

/** 2026-10-28: Mange pladser i én anmodning - kontrolleret række for række, mens der skrives. */
function SitesPaste({ kind, customerId = "", customerName = "", text, setText, onChecked }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const nameOk = kind !== "customer" || customerName.trim().length >= 2;
  useEffect(() => {
    const rows = parseSites(text);
    if (!rows.length || !nameOk) { setRes(null); onChecked(null); return undefined; }
    let live = true;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await axios.post(`${API}/logistics/master-requests`, { kind, customer_id: customerId, customer_name: customerName, sites: rows, preview: true });
        if (live) { setRes(data); onChecked(data); }
      } catch (e) { if (live) { setRes({ error: formatApiError(e) }); onChecked(null); } }
      if (live) setBusy(false);
    }, 450);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, kind, customerId, customerName, nameOk]);
  const n = parseSites(text).length;
  return (
    <div className="space-y-2">
      <textarea className={`${input} h-28 font-mono text-xs`} value={text} onChange={(e) => setText(e.target.value)} data-testid="req-sites-text"
        placeholder={"Én plads pr. linje - kopiér fra Excel (navn, vej og nr., postnr. og by) eller skriv med semikolon:\nKøge Genbrugsplads; Søndre Viaduktvej 1; 4600 Køge\nHårlev Genbrugsplads; Industrivej 3; 4652 Hårlev"} />
      {n > 0 && !nameOk && <p className="text-[11px] text-amber-700">Skriv kundens navn, så kontrolleres listen.</p>}
      {busy && <p className="text-[11px] text-slate-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Kontrollerer {n} {n === 1 ? "plads" : "pladser"} ...</p>}
      {res?.error && <p className="text-xs text-red-700">{res.error}</p>}
      {res?.customer_warning && <p className="text-xs text-amber-800 bg-amber-50 dark:bg-amber-900/30 rounded-lg px-2 py-1">{res.customer_warning}</p>}
      {res?.sites && <SiteRowsTable rows={res.sites} />}
    </div>
  );
}

const nPl = (n) => `${n} ${n === 1 ? "plads" : "pladser"}`;

const sitesSummary = (chk) => {
  const rows = chk?.sites || [];
  const c = (k) => rows.filter((r) => r.status === k).length;
  return { rows, ok: c("ok"), exists: c("exists"), errors: c("error") };
};

/* ------------------------------- Modtageanlæg ------------------------------- */
function FacilityForm({ item, all, onCancel, onSaved }) {
  const [f, setF] = useState({ label: item?.label || "", addr: { street: item?.street || "", postal: item?.postcode || "", city: item?.city || "" },
    aliases: item?.aliases || [], alias: "", note: item?.note || "",
    // 2026-10-28: Én måde at skrive anlæggets tider på (pr. ugedag). Et ældre anlæg med ét tidsrum vises som det samme tidsrum alle dage.
    weekly_hours: weekFromRange(item) });
  const [saving, setSaving] = useState(false);
  const addAlias = () => {
    const a = f.alias.trim();
    if (a && !f.aliases.some((x) => norm(x) === norm(a))) setF({ ...f, aliases: [...f.aliases, a], alias: "" });
    else setF({ ...f, alias: "" });
  };
  const save = async () => {
    if (!f.label.trim()) { toast.error("Skriv anlæggets navn"); return; }
    const other = item && all.find((x) => x.id !== item.id && [x.label, ...(x.aliases || [])].some((n) => norm(n) === norm(f.label)));
    if (other && !window.confirm(`"${f.label}" er ${other.label}. Læg de to anlæg sammen?\n\nBestillinger, ture og opsætninger flyttes til ${other.label}, og "${item.label}" bliver et andet navn for det, så Pickup og mails genkender det næste gang.`)) return;
    setSaving(true);
    try {
      const body = { label: f.label, street: f.addr.street || "", postcode: f.addr.postal || "", city: f.addr.city || "", aliases: f.aliases,
        open_from: "", open_to: "", note: f.note, weekly_hours: f.weekly_hours };
      const { data } = item ? await axios.put(`${API}/logistics/facilities/${item.id}`, body) : await axios.post(`${API}/logistics/facilities`, body);
      toast.success(`${data.label} er ${data.merged ? "lagt sammen" : "gemt"}${data.updated_tours ? ` - ${data.updated_tours} åbne ture fik den nye adresse` : ""}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return (
    <div className="p-3 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-3" data-testid="facility-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-3">
          <div><span className={label}>Navn</span>
            <input className={input} value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Fx Geminor Køge Havn" autoFocus={!item} data-testid="facility-label" /></div>
          <div><span className={label}>Andre navne (fx som Pickup eller en mail skriver det)</span>
            <div className="flex gap-2">
              <input className={input} value={f.alias} onChange={(e) => setF({ ...f, alias: e.target.value })} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(); } }} placeholder="Fx Geminor Køge" data-testid="facility-alias" />
              <button type="button" className={btnGhost} onClick={addAlias}>Tilføj</button>
            </div>
            {f.aliases.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1.5">{f.aliases.map((a) => (
              <span key={a} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-xs">{a}
                <button type="button" aria-label="Fjern" onClick={() => setF({ ...f, aliases: f.aliases.filter((x) => x !== a) })}><X className="w-3 h-3" /></button></span>))}</div>}
          </div>
          {/* 2026-10-28: Kun ét sted at skrive anlæggets tider (før både ét tidsrum og pr. ugedag). Anlæggets egne
              modtagetider - bruges på egne ture og fragt og som udgangspunkt i "Forslag til kalender".
              En leveringskalender pr. plads og materiale går forud for den plads. */}
          <div><span className={label}>Anlæggets åbningstider (valgfri)</span>
            <WeeklyHours value={f.weekly_hours} onChange={(w) => setF({ ...f, weekly_hours: w })} testid="facility-week" />
            <p className="text-[11px] text-slate-500 mt-1">Skrives kun her: bruges på egne ture og fragt - og i leveringskalendere, hvor tiderne står tomme (så en ændring her gælder dem alle). Står der tider i en kalender, gælder de for den plads.</p></div>
        </div>
        <div className="space-y-3">
          <AddressField label="Adresse" value={f.addr} onChange={(v) => setF({ ...f, addr: v })} testid="facility-addr" inputCls={input} />
          <div><span className={label}>Note til chaufføren (valgfri)</span><input className={input} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Fx indkørsel ved port 2, vejning først" /></div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={onCancel}><X className="w-4 h-4" /> Annuller</button>
        <button className={btnPrimary} disabled={saving} onClick={save} data-testid="facility-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Gem</button>
      </div>
    </div>
  );
}

function Facilities({ list, canEdit, onChanged }) {
  const [edit, setEdit] = useState(null);
  const [excel, setExcel] = useState(false);   // 2026-10-28: mange anlæg på én gang
  const remove = async (f) => {
    if (!window.confirm(`Slet ${f.label}?`)) return;
    try { await axios.delete(`${API}/logistics/facilities/${f.id}`); toast.success(`${f.label} er slettet`); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const missing = list.filter((f) => f.missing_address).length;
  return (
    <section className={card} data-testid="facilities">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700">
        <div>
          <h2 className="font-bold text-lg flex items-center gap-2"><Factory className="w-5 h-5 text-sky-600" /> Modtageanlæg</h2>
          <p className="text-xs text-slate-500">Jeres ÉN liste over steder, læsset køres hen (forbrændings- og sorteringsanlæg ...) - fælles for alle jeres ture:
            egne ture og fragt (også for egne kunder uden pladser) og hos egne kunder med pladser. Et anlæg er det samme sted, uanset hvis læs
            det er - så det oprettes kun én gang, og prisen pr. leveringsadresse og rapporterne samler det hele. Ved hvert anlæg står, hvor det bruges.</p>
        </div>
        <div className="flex items-center gap-2">
          {missing > 0 && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-800 bg-amber-50 dark:bg-amber-900/30 dark:text-amber-200 px-2.5 py-1 rounded-full"><AlertTriangle className="w-3.5 h-3.5" /> {missing} mangler adresse</span>}
          {canEdit && edit === null && <button className={btnGhost} onClick={() => setExcel(true)} data-testid="facility-excel"><FileSpreadsheet className="w-4 h-4" /> Excel</button>}
          {canEdit && edit === null && <button className={btnPrimary} onClick={() => setEdit("new")} data-testid="facility-new"><Plus className="w-4 h-4" /> Nyt anlæg</button>}
        </div>
      </div>
      {excel && <ImportDialog kind="facilities" title="Modtageanlæg (Excel)" onClose={() => setExcel(false)} onDone={onChanged} />}
      <div className="p-4 space-y-2">
        {edit === "new" && <FacilityForm all={list} onCancel={() => setEdit(null)} onSaved={() => { setEdit(null); onChanged(); }} />}
        {list.length === 0 && edit !== "new" && <p className="text-sm text-slate-500">Ingen modtageanlæg endnu. De oprettes også, når I indsætter fra Pickup ("Til") eller en mail ("Modtageanlæg").</p>}
        {list.map((f) => edit === f.id ? <FacilityForm key={f.id} item={f} all={list} onCancel={() => setEdit(null)} onSaved={() => { setEdit(null); onChanged(); }} /> : (
          <div key={f.id} className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-slate-100 dark:border-slate-700 px-3 py-2" data-testid={`facility-${norm(f.label).replace(/\s+/g, "-")}`}>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Factory className="w-4 h-4 text-sky-600 shrink-0" />
                <span className="font-semibold">{f.label}</span>
                {f.missing_address && <span className={amber}>Mangler adresse</span>}
                {(f.weekly_hours ? weekSummary(f.weekly_hours) : hours(f)) && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3 h-3" />{f.weekly_hours ? weekSummary(f.weekly_hours) : hours(f)}</span>}
                {(f.orders + f.tours) > 0 && <span className="text-[11px] text-slate-500" data-testid="facility-usage">
                  {[f.tours ? `${f.tours} egne ${f.tours === 1 ? "tur" : "ture"}` : "", f.orders ? `${f.orders} ${f.orders === 1 ? "bestilling" : "bestillinger"} hos kunder med pladser` : ""].filter(Boolean).join(" · ")}</span>}
              </div>
              <div className="text-xs text-slate-500 ml-6">{f.missing_address ? "Kun navnet - chaufføren får ingen vej og nr." : f.line}
                {(f.aliases || []).length > 0 && ` · også: ${f.aliases.join(", ")}`}
                {(f.used_by || []).length > 0 && ` · hos ${f.used_by.map((u) => `${u.customer} (${u.sites} ${u.sites === 1 ? "plads" : "pladser"})`).join(", ")}`}</div>
            </div>
            {canEdit && (
              <div className="flex items-center gap-1 self-end sm:self-auto">
                <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" title="Ret (eller læg sammen med et andet)" onClick={() => setEdit(f.id)} data-testid="facility-edit"><Edit2 className="w-4 h-4" /></button>
                {!f.in_use && <button className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600" title="Slet" onClick={() => remove(f)}><Trash2 className="w-4 h-4" /></button>}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* -------------------------- Egne kunder med pladser ------------------------- */
function SiteForm({ cust, site, onCancel, onSaved, onBulk }) {
  const [f, setF] = useState({ name: site?.name || "", address: site && !site.address_missing ? site.address : "", city: site?.city || "",
    // 2026-10-28: Pladsens tider pr. ugedag (som anlæg og egne lokationer) - et ældre tidsrum vises som alle dage.
    weekly_hours: weekFromRange(site) });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      if (site) {
        const { data } = await axios.put(`${API}/logistics/external-customers/${cust.id}/sites/${site.id}`,
          { address: f.address, city: f.city, weekly_hours: f.weekly_hours || {} });
        toast.success(`${site.name} er gemt${data.updated_tours ? ` - ${data.updated_tours} åbne ture fik den nye adresse` : ""}`);
      } else {
        // 2026-10-27: ContainerFlow opretter pladsen (så den kun findes én gang) og sætter jer på som vognmand.
        if (!f.name.trim()) { toast.error("Skriv pladsens navn"); setSaving(false); return; }
        const r = await requestExternal({ kind: "site", customer_id: cust.id, site_name: f.name, address: f.address, city: f.city });
        toast.success(r.duplicate ? `${f.name} er allerede bedt om - ContainerFlow opretter den` : `Anmodning sendt: ContainerFlow opretter ${f.name} hos ${cust.name}`);
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return (
    <div className="p-3 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-3" data-testid="ext-site-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {site ? (
          <div><span className={label}>Navn</span><div className="text-sm font-semibold py-2">{site.name}</div>
            <p className="text-[11px] text-slate-500">Navnet er fælles og rettes af ContainerFlow. Kalder Pickup eller mails den noget andet, så tilføj det under "Jeres navne".</p></div>
        ) : (
          <div><span className={label}>Pladsens navn</span>
            <input className={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Fx Glostrup Genbrugsstation" autoFocus data-testid="ext-site-name" /></div>
        )}
        <div><span className={label}>Adresse</span>
          <input className={input} value={f.address} readOnly={!!site?.shared} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Vej og nr." data-testid="ext-site-address" /></div>
        <div><span className={label}>Postnr. og by</span>
          <input className={input} value={f.city} readOnly={!!site?.shared} onChange={(e) => setF({ ...f, city: e.target.value })} placeholder="Fx 4600 Køge" data-testid="ext-site-city" /></div>
      </div>
      {!site && <p className="text-[11px] text-slate-500">ContainerFlow opretter pladsen og sætter jer på som vognmand - så findes den kun én gang, også når andre vognmænd kører der.
        {onBulk && <> <button type="button" className="font-semibold underline text-emerald-700 dark:text-emerald-400" onClick={onBulk} data-testid="ext-site-bulk">Mange pladser? Indsæt en liste</button></>}</p>}
      {site?.shared && <p className="text-[11px] rounded-lg bg-violet-50 dark:bg-violet-950/30 text-violet-800 dark:text-violet-200 px-2.5 py-1.5" data-testid="ext-site-shared">
        Pladsen deles med en anden vognmand - adresse og åbningstider er fælles og rettes af ContainerFlow (skriv til os).</p>}
      {site && !site.shared && <div>
        <span className={label}>Pladsens åbningstider (valgfri)</span>
        <WeeklyHours value={f.weekly_hours} onChange={(w) => setF({ ...f, weekly_hours: w })} testid="ext-site-week" />
        <p className="text-[11px] text-slate-500 mt-1">Bruges som tidsrum på turen den ugedag, når bestillingen ikke selv har et (fx fra Pickup).</p>
      </div>}
      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={onCancel}><X className="w-4 h-4" /> Annuller</button>
        <button className={btnPrimary} disabled={saving || !!site?.shared} onClick={save} data-testid="ext-site-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {site ? "Gem" : "Send anmodning"}</button>
      </div>
    </div>
  );
}

/**
 * 2026-10-27: Pladsens (eller jeres egen lokations) åbnings- og helligdagskalendere -
 * samme sider som hos en kunde i ContainerFlow. Pr. materiale og dag: primær
 * leveringsadresse og alternativer med egne tider, valgt fra jeres modtageanlæg.
 * `free`: en egen lokation har ingen containerliste - materialerne vælges frit.
 */
/**
 * 2026-10-27: Forslag til kalenderen ud fra pladsens bestillinger. Pickup, mails og Excel
 * siger, hvor læsset skal hen - pr. materiale bruges det anlæg, der oftest er kørt til.
 */
function CalendarSuggest({ code, onApplied }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    axios.post(`${API}/logistics/calendars/suggest`, { location_code: code }).then((r) => setList(r.data?.suggestions || [])).catch(() => setList([]));
  }, [code]);
  useEffect(() => { load(); }, [load]);
  const todo = (list || []).filter((x) => !x.has_calendar);
  if (!todo.length) return null;
  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/logistics/calendars/suggest`, { location_code: code, apply: true });
      toast.success(`${data.materials} materialer har fået en kalender (${data.calendars} ny${data.calendars === 1 ? "" : "e"}) - ret dem gerne nedenfor`);
      load(); onApplied?.();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/30 p-3 space-y-2" data-testid={`calendar-suggest-${code}`}>
      <div className="text-sm font-semibold flex items-center gap-1.5"><Lightbulb className="w-4 h-4 text-sky-600" /> Forslag ud fra bestillingerne (seneste halve år)</div>
      <ul className="text-xs text-slate-700 dark:text-slate-200 space-y-0.5">
        {todo.map((x) => <li key={x.material}><b>{x.material}</b> → {x.label} <span className="text-slate-500">({x.orders} af {x.of} bestillinger)</span></li>)}
      </ul>
      <p className="text-[11px] text-slate-500">Én åbningstidskalender pr. anlæg. Tiderne står tomme = anlæggets egne (også lukkede dage), så en ændring af anlæggets tider gælder kalenderen. Ret dage, alternativer og helligdage bagefter.</p>
      <button type="button" className={btnPrimary} disabled={busy} onClick={apply} data-testid="calendar-suggest-apply">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Opret kalendere for {todo.length} {todo.length === 1 ? "materiale" : "materialer"}</button>
    </div>
  );
}

/** Kalenderens status på en plads/lokation: hvilke materialer har en leveringskalender. */
export function CalStatus({ mats = [] }) {
  return mats.length ? (
    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200" title={mats.join(", ")}
      data-testid="cal-status">Kalender: {mats.length <= 2 ? mats.join(", ") : `${mats.length} materialer`}</span>
  ) : (
    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300" data-testid="cal-status"
      title="Valgfri: kun nødvendig, når der oprettes ture uden Pickup eller mail">Ingen leveringskalender</span>
  );
}

export function SiteCalendars({ code, free = false, siblings = [], excel = null }) {
  const [sugg, setSugg] = useState(free ? null : undefined);
  // 2026-10-28: Mange pladser på én gang - Excel (hent nuværende, ret, importér igen).
  const [excelOpen, setExcelOpen] = useState(false);
  const [rev, setRev] = useState(0);   // genindlæs kalenderne efter et forslag
  const [extra, setExtra] = useState([]);
  useEffect(() => {
    if (free) axios.get(`${API}/logistics/material-suggestions`).then((r) => setSugg(r.data?.materials || [])).catch(() => setSugg([]));
  }, [free]);
  if (sugg === null) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;
  const p = { locationCode: code, addressesUrl: `${API}/logistics/calendar-addresses?location_code=${encodeURIComponent(code)}`, siblings,
    ...(free ? { materialSuggestions: sugg, extraMaterials: extra, onAddMaterial: (m) => setExtra((x) => [...x, m]) } : {}) };
  return (
    <div className="space-y-3" data-testid={`site-calendars-${code}`}>
      <p className="text-xs text-slate-500">Pr. materiale: hvilket modtageanlæg der er primært hver ugedag og helligdag - og alternativerne med egne tider.
        De vises ved bestilling og for chaufføren, og er det primære lukket, bruges det første åbne alternativ. Anlæg, der ikke står på listen endnu, oprettes under Modtageanlæg.</p>
      <CalendarSuggest code={code} onApplied={() => setRev((r) => r + 1)} />
      <HoursSection key={`h${rev}`} {...p} />
      <HolidaySection key={`d${rev}`} {...p} />
      {/* Kopiér kalenderne (primær + alternativer) til kundens andre pladser - fx mange ens pladser. */}
      <CalendarsCopy code={code} siblings={siblings} url={`${API}/logistics/calendars/copy`} onDone={() => setRev((r) => r + 1)} />
      {excel && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <button type="button" className={btnGhost} onClick={() => setExcelOpen(true)} data-testid={`calendars-excel-${code}`}>
            <FileSpreadsheet className="w-4 h-4" /> Excel: åbningstider og helligdage</button>
          Alle {free ? "lokationer" : "pladser"} i én fil - hent de nuværende, ret eller kopiér rækker til andre, og importér igen.
        </div>
      )}
      {excelOpen && <ImportDialog kind={excel.kind} companyId={excel.companyId || ""} title="Åbningstider og helligdage (Excel)"
        onClose={() => setExcelOpen(false)} onDone={() => setRev((r) => r + 1)} />}
    </div>
  );
}

/**
 * 2026-10-27: Hvad der skal til, før kunden kan køres for - i rækkefølge, med status.
 * Før skulle man selv vide, at leveringsadresser og åbningstider sidder på hver plads.
 */
function SetupSteps({ cust, canDelivery, onNewSite }) {
  const [all, setAll] = useState(false);
  const sites = cust.sites;
  const steps = [
    ["Pladser", sites.length > 0, sites.length ? `${sites.length} ${sites.length === 1 ? "plads" : "pladser"}` : "ContainerFlow opretter pladserne og sætter jer på - bed om dem med \"Anmod om plads\""],
    ["Materialer og containere", sites.length > 0 && sites.every((x) => x.materials.length), "Pr. plads (\"+ Materiale\"), fra Excel - eller fra Pickup og mails"],
    // 2026-10-28: Valgfri - Pickup og mails siger selv, hvor læsset skal hen. Kun nødvendig, når
    // der oprettes ture uden dem (Bestil for kunden, chaufførens + Tur, faste tømninger).
    ...(canDelivery ? [["Leveringsadresser og åbningstider (valgfri)", sites.length > 0 && sites.every((x) => (x.cal_materials || []).length),
      "Kun hvis I opretter ture uden Pickup eller mail (Bestil for kunden, chaufførens + Tur, faste tømninger) - Pickup og mails siger selv, hvor læsset skal hen", true]] : []),
    ["Priser", cust.price_rules > 0, cust.price_rules ? `${cust.price_rules} prisregler` : "Økonomi › Kunder & priser › \"Sæt egen pris\" - ellers gælder jeres generelle priser"],
  ];
  const next = steps.findIndex(([, done, , optional]) => !done && !optional);
  if (next < 0) return null;
  // Kun næste trin (med knappen, når det er pladserne) - resten bag "Alle trin", så listen ikke fylder ved hver kunde.
  const shown = all ? steps.map((x, i) => [x, i]) : [[steps[next], next]];
  return (
    <div className="rounded-xl bg-slate-50 dark:bg-slate-900 p-2.5 text-xs space-y-1.5" data-testid={`ext-setup-${cust.id}`}>
      <ol className={`grid grid-cols-1 ${all ? "md:grid-cols-2" : ""} gap-1.5`}>
        {shown.map(([[title, done, hint], i]) => (
          <li key={title} className="flex items-start gap-1.5">
            {done ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
            <span className="min-w-0"><b className={done ? "text-slate-500" : "text-slate-800 dark:text-slate-100"}>{all ? "" : "Næste: "}{i + 1}. {title}</b>
              <span className="block text-slate-500">{hint}</span>
              {!all && i === 0 && onNewSite && <button type="button" className={`${btnPrimary} mt-1.5 !py-1.5 !text-xs`} onClick={onNewSite} data-testid={`ext-setup-new-site-${cust.id}`}><Plus className="w-3.5 h-3.5" /> Anmod om plads</button>}
            </span>
          </li>
        ))}
      </ol>
      <button type="button" className="text-[11px] font-semibold text-slate-500 underline" onClick={() => setAll((v) => !v)}>{all ? "Kun næste trin" : `Alle ${steps.length} trin`}</button>
    </div>
  );
}

/**
 * Materialer på pladsen + tilføj et materiale (og evt. en container) direkte.
 * 2026-10-28: "Ret" viser jeres containere pr. materiale - en container eller et helt materiale kan
 * fjernes (før kunne et materiale kun tilføjes). Kun jeres egne; bestillinger og ture bevarer deres.
 */
export function SiteMaterials({ base, canEdit, site, materialSuggestions, onChanged }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ material: "", container: "", capacity: "" });
  const [saving, setSaving] = useState(false);
  const [manage, setManage] = useState(null);   // null = lukket, [] = indlæst
  // 2026-10-28: Også egne lokationer (base = /logistics/own-locations/<id>) - de har ingen materialeliste i
  // pladslisten, så den hentes her.
  const [own, setOwn] = useState(null);
  const loadOwn = useCallback(() => {
    if (site.materials) return;
    axios.get(`${base}/containers`).then((r) => setOwn(r.data?.materials || [])).catch(() => setOwn([]));
  }, [base, site.materials]);
  useEffect(() => { loadOwn(); }, [loadOwn]);
  const mats = site.materials || (own || []).map((m) => m.material);
  const changed = () => { onChanged?.(); loadOwn(); };
  const loadManage = useCallback(() => {
    axios.get(`${base}/containers`).then((r) => setManage(r.data?.materials || [])).catch((e) => { toast.error(formatApiError(e)); setManage(null); });
  }, [base]);
  const add = async () => {
    if (!f.material.trim()) { toast.error("Skriv materialet"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${base}/containers`, f);
      toast.success(data.container ? `Container ${data.container} (${data.material}) er tilføjet` : `${data.material} er tilføjet`);
      setF({ material: f.material, container: "", capacity: f.capacity });
      changed();
      if (manage) loadManage();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  const removeMaterial = async (m) => {
    const n = m.containers.length;
    if (!window.confirm(`Fjern ${m.material} fra ${site.name}?\n\n${n ? `Jeres ${n} ${n === 1 ? "container" : "containere"} på materialet fjernes også. ` : ""}`
      + "Bestillinger og ture beholder materialet, og åbne bestillinger køres som planlagt. Det kan tilføjes igen.")) return;
    try {
      const { data } = await axios.delete(`${base}/materials`, { params: { material: m.material } });
      toast.success(`${data.removed} er fjernet fra ${site.name}${data.open_orders ? ` - ${data.open_orders} åbne bestillinger køres som planlagt` : ""}`);
      loadManage(); changed();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeContainer = async (m, c) => {
    const what = c.number ? `container ${c.number}` : `${c.label} (uden nr.)`;
    if (!window.confirm(`Fjern ${what} (${m.material}) fra ${site.name}? Bestillinger beholder nummeret.`)) return;
    try {
      await axios.delete(`${base}/containers`, { params: { material: m.material, label: c.label } });
      toast.success(`${what} er fjernet`);
      loadManage(); changed();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="ml-6 space-y-1.5" data-testid={`ext-materials-${site.location_code}`}>
      <div className="flex flex-wrap items-center gap-1">
        {mats.map((m) => <span key={m} className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-slate-700">{m}</span>)}
        {canEdit && !open && <button type="button" className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-0.5"
          onClick={() => setOpen(true)} data-testid={`ext-material-add-${site.location_code}`}><Plus className="w-3 h-3" /> Materiale / container</button>}
        {canEdit && mats.length > 0 && <button type="button" className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 inline-flex items-center gap-0.5 ml-1"
          onClick={() => (manage ? setManage(null) : loadManage())} data-testid={`ext-material-manage-${site.location_code}`}>
          <Edit2 className="w-3 h-3" /> {manage ? "Luk" : "Ret / fjern"}</button>}
      </div>
      {open && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="w-44"><SuggestInput className={input} value={f.material} onChange={(v) => setF({ ...f, material: v })} options={materialSuggestions}
            placeholder="Materiale, fx Pap" testid={`ext-material-name-${site.location_code}`} /></div>
          <input className={`${input.replace("w-full ", "")} w-40`} value={f.container} onChange={(e) => setF({ ...f, container: e.target.value })} placeholder="Container nr. (valgfri)" />
          <input className={`${input.replace("w-full ", "")} w-28`} value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} placeholder="Fx 20 m³" />
          <button type="button" className={btnPrimary} disabled={saving} onClick={add} data-testid={`ext-material-save-${site.location_code}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Tilføj</button>
          <button type="button" className={btnGhost} onClick={() => setOpen(false)}>Luk</button>
        </div>
      )}
      {manage && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700 text-xs" data-testid={`ext-material-list-${site.location_code}`}>
          {manage.length === 0 && <p className="px-3 py-2 text-slate-500">Ingen materialer.</p>}
          {manage.map((m) => (
            <div key={m.material} className="px-3 py-2 flex flex-wrap items-center gap-1.5">
              <b className="mr-1">{m.material}</b>
              {m.containers.length === 0 && <span className="text-slate-500">ingen containere</span>}
              {m.containers.map((c) => (
                <span key={c.label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700">
                  {c.number || <i className="text-slate-500">{c.label} (uden nr.)</i>}{c.capacity ? ` · ${c.capacity}` : ""}
                  <button type="button" aria-label={`Fjern ${c.number || c.label}`} className="text-slate-500 hover:text-red-600" onClick={() => removeContainer(m, c)}
                    data-testid={`ext-container-remove-${c.number || c.label}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
              <span className="flex-1" />
              {m.fixed > 0 && <span className="text-[11px] text-amber-700" title="Slet de faste tømninger først (Ture › Faste tømninger)">{m.fixed} faste tømninger</span>}
              <button type="button" className="inline-flex items-center gap-1 text-red-600 font-semibold disabled:opacity-40" disabled={m.fixed > 0}
                onClick={() => removeMaterial(m)} data-testid={`ext-material-remove-${m.material}`}><Trash2 className="w-3 h-3" /> Fjern materialet</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SiteNames({ cust, site, names, onChanged }) {
  const [adding, setAdding] = useState(false);
  const [txt, setTxt] = useState("");
  const mine = names.filter((n) => n.kind === "plads" && n.to === site.location_code);
  const add = async () => {
    try {
      await axios.post(`${API}/logistics/import-names`, { customer_id: cust.id, kind: "plads", name: txt, to: site.location_code });
      toast.success(`"${txt.trim()}" genkendes nu som ${site.name}`); setTxt(""); setAdding(false); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (n) => {
    try { await axios.delete(`${API}/logistics/import-names`, { params: { customer_id: cust.id, kind: "plads", key: n.key } }); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="ml-6 flex flex-wrap items-center gap-1 text-[11px]" data-testid={`ext-names-${site.location_code}`}>
      <span className="text-slate-500">Jeres navne i filer:</span>
      {mine.length === 0 && !adding && <span className="text-slate-400">ingen (pladsens navn genkendes)</span>}
      {mine.map((n) => (
        <span key={n.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-900/30 text-sky-900 dark:text-sky-100">{n.name}
          {cust.can_edit && <button type="button" aria-label="Fjern" onClick={() => del(n)}><X className="w-3 h-3" /></button>}</span>
      ))}
      {cust.can_edit && !adding && <button type="button" className="font-semibold text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-0.5"
        onClick={() => setAdding(true)} data-testid={`ext-name-add-${site.location_code}`}><Plus className="w-3 h-3" /> navn</button>}
      {adding && <>
        <input className={`${input.replace("w-full ", "")} w-48 !py-1`} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Fx Køge GP"
          onKeyDown={(e) => { if (e.key === "Enter") add(); }} autoFocus data-testid={`ext-name-input-${site.location_code}`} />
        <button type="button" className={`${btnPrimary} !py-1 !px-2 !text-xs`} disabled={txt.trim().length < 2} onClick={add} data-testid={`ext-name-save-${site.location_code}`}>Tilføj</button>
        <button type="button" className="underline" onClick={() => { setAdding(false); setTxt(""); }}>Luk</button>
      </>}
    </div>
  );
}

/** 2026-10-28: Mange nye pladser hos en kunde i én anmodning (fx når kunden sættes op). */
function BulkSites({ cust, onCancel, onSaved }) {
  const [text, setText] = useState("");
  const [chk, setChk] = useState(null);
  const [saving, setSaving] = useState(false);
  const sum = sitesSummary(chk);
  const save = async () => {
    setSaving(true);
    try {
      const r = await requestExternal({ kind: "site", customer_id: cust.id, sites: parseSites(text) });
      toast.success(r.added !== undefined ? (r.added ? `${r.added} pladser er lagt til jeres åbne anmodning` : "Pladserne er allerede bedt om")
        : `Anmodning sendt: ContainerFlow gennemgår ${nPl(sum.rows.length)} hos ${cust.name}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return (
    <div className="p-3 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-2" data-testid="ext-bulk-sites">
      <span className={label}>Nye pladser hos {cust.name}</span>
      <SitesPaste kind="site" customerId={cust.id} text={text} setText={setText} onChecked={setChk} />
      <p className="text-[11px] text-slate-500">Én anmodning med hele listen. ContainerFlow kontrollerer den igen og opretter pladserne (og sætter jer på som vognmand) - findes en plads allerede, sættes I blot på den.</p>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {sum.rows.length > 0 && <span className="text-xs text-slate-500 mr-auto">{sum.ok} nye · {sum.exists} findes{sum.errors ? ` · ${sum.errors} skal rettes` : ""}</span>}
        <button className={btnGhost} onClick={onCancel}><X className="w-4 h-4" /> Annuller</button>
        <button className={btnPrimary} disabled={saving || !sum.rows.length || sum.errors > 0} onClick={save} data-testid="ext-bulk-send">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Send anmodning{sum.rows.length ? ` (${sum.rows.length})` : ""}</button>
      </div>
    </div>
  );
}

function CustomerCard({ cust, canDelivery, materialSuggestions = [], onChanged }) {
  const [edit, setEdit] = useState(null);   // site id | "new"
  const [openSite, setOpenSite] = useState(null);
  const [importing, setImporting] = useState(false);
  const missing = cust.sites.filter((s) => s.address_missing).length;
  const done = () => { setEdit(null); onChanged(); };
  const [names, setNames] = useState([]);
  const loadNames = useCallback(() => {
    axios.get(`${API}/logistics/import-names`, { params: { customer_id: cust.id } }).then((r) => setNames(r.data || [])).catch(() => setNames([]));
  }, [cust.id]);
  useEffect(() => { loadNames(); }, [loadNames]);
  // Oprettet af ContainerFlow (eller andre kører for den): kun jeres del fjernes - kunden og pladserne bliver.
  const partOnly = cust.shared || cust.by_cf;
  // 2026-10-27: Slet (fx oprettet ved en fejl) - eller skjul, når der er historik.
  const remove = async () => {
    if (!window.confirm(partOnly
      ? `Fjern ${cust.name} hos jer? Jeres materialer, containere, kalendere, leveringsadresser og navne på pladserne slettes. Kunden og pladserne bliver (de oprettes og rettes af ContainerFlow).`
      : `Slet ${cust.name}? Pladser, materialer, containere og kalendere slettes også.`)) return;
    try {
      await axios.delete(`${API}/logistics/external-customers/${cust.id}`);
      toast.success(partOnly ? `${cust.name} er fjernet hos jer` : `${cust.name} er slettet`); onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "in_use") {
        if (!window.confirm(`${d.message}\n\nSkjul ${cust.name}?`)) return;
        try { await axios.delete(`${API}/logistics/external-customers/${cust.id}`, { params: { mode: "hide" } }); toast.success(`${cust.name} er skjult`); onChanged(); }
        catch (e2) { toast.error(formatApiError(e2)); }
      } else toast.error(formatApiError(e));
    }
  };
  // 2026-10-28: I kører ikke længere på én plads. Pladsen er fælles (ContainerFlow) - jeres del fjernes;
  // med historik skjules den i stedet hos jer (kan vises igen). Andre vognmænd mærker intet.
  const leaveSite = async (site) => {
    if (!window.confirm(`Kører I ikke længere på ${site.name}?\n\nJeres materialer, containere, kalendere, leveringsadresser og navne på pladsen `
      + "fjernes. Pladsen selv bliver (ContainerFlow har den, og andre vognmænd kan køre der).")) return;
    try {
      const { data } = await axios.delete(`${API}/logistics/external-customers/${cust.id}/sites/${site.id}`);
      toast.success(data.customer_removed ? `${site.name} er fjernet - og ${cust.name} er ikke længere jeres kunde (det var den sidste plads)` : `${site.name} er fjernet hos jer`);
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.code === "in_use") {
        if (!window.confirm(`${d.message}\n\nSkjul ${site.name} hos jer?`)) return;
        try {
          await axios.delete(`${API}/logistics/external-customers/${cust.id}/sites/${site.id}`, { params: { mode: "hide" } });
          toast.success(`${site.name} er skjult hos jer - de faste tømninger holder pause`); onChanged();
        } catch (e2) { toast.error(formatApiError(e2)); }
      } else toast.error(formatApiError(e));
    }
  };
  const showSite = async (site) => {
    try {
      await axios.post(`${API}/logistics/external-customers/${cust.id}/sites/${site.id}/show`);
      toast.success(`${site.name} vises igen`); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border-t border-slate-100 dark:border-slate-700 first:border-t-0 px-4 py-4 space-y-3" data-testid={`ext-customer-${cust.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Building2 className="w-4 h-4 text-violet-600" />
        <span className="font-bold">{cust.name}</span>
        <span className="text-xs text-slate-500">{cust.sites.length} {cust.sites.length === 1 ? "plads" : "pladser"}</span>
        {missing > 0 && <span className={amber}>{missing} mangler adresse</span>}
        <span className="flex-1" />
        {cust.can_edit && edit === null && <button className={btnPrimary} onClick={() => setEdit("new")} data-testid="ext-new-sites"><Plus className="w-4 h-4" /> Anmod om plads</button>}
        {cust.can_edit && <button className={btnGhost} onClick={() => setImporting(true)} title="Mange materialer og containere på én gang" data-testid="ext-containers-import"><FileSpreadsheet className="w-4 h-4" /> Importér fra Excel</button>}
        {cust.can_edit && <button className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600" title={partOnly ? "Fjern kunden hos jer (eller skjul den, når der er historik)" : "Slet kunden (eller skjul den, når der er historik)"} onClick={remove} data-testid={`ext-delete-${cust.id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
      {cust.shared && (
        <p className="text-[11px] text-slate-600 dark:text-slate-300 rounded-lg bg-violet-50 dark:bg-violet-950/30 px-2.5 py-1.5" data-testid={`ext-shared-${cust.id}`}>
          Andre vognmænd kører også for {cust.name}. I ser kun de pladser, I selv kører på, og jeres egne materialer, containere, ture og priser.
          Kører I på samme plads, er pladsens adresse og åbningstider fælles (dem retter ContainerFlow), mens materialer, kalendere og
          leveringsadresser er hver vognmands egne - og hvert materiale har én vognmand på pladsen.
        </p>
      )}
      {importing && <ImportDialog kind="c_containers" companyId={cust.id} title={`Materialer og containere - ${cust.name}`} onClose={() => setImporting(false)} onDone={onChanged} />}
      <SetupSteps cust={cust} canDelivery={canDelivery} onNewSite={cust.can_edit && edit === null ? () => setEdit("new") : null} />
      <div className="space-y-2">
        {edit === "new" && <SiteForm cust={cust} onCancel={() => setEdit(null)} onSaved={done} onBulk={() => setEdit("bulk")} />}
        {edit === "bulk" && <BulkSites cust={cust} onCancel={() => setEdit(null)} onSaved={done} />}

        {cust.sites.map((s) => edit === s.id ? <SiteForm key={s.id} cust={cust} site={s} onCancel={() => setEdit(null)} onSaved={done} /> : (
          <div key={s.id} className="rounded-xl border border-slate-100 dark:border-slate-700 px-3 py-2 space-y-2" data-testid={`ext-site-${s.location_code}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span className="font-semibold">{s.name}</span>
                  {s.location_code && <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-mono">{s.location_code}</span>}
                  {s.address_missing && <span className={amber}>Mangler adresse</span>}
                  {(s.weekly_hours ? weekSummary(s.weekly_hours) : hours(s)) && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Clock className="w-3 h-3" />{s.weekly_hours ? weekSummary(s.weekly_hours) : hours(s)}</span>}
                </div>
                <div className="text-xs text-slate-500 ml-6">{s.address_missing ? "Adressen er kun pladsens navn (fx oprettet fra Pickup) - skriv vej og nr., så chaufføren finder den" : [s.address, s.city].filter(Boolean).join(", ")}
                  {" · "}{s.materials.length} {s.materials.length === 1 ? "materiale" : "materialer"} · {s.containers} {s.containers === 1 ? "container" : "containere"}</div>
              </div>
              <div className="flex items-center gap-1 self-end sm:self-auto">
                {canDelivery && s.location_code && <CalStatus mats={s.cal_materials || []} />}
                {canDelivery && s.location_code && <button className={btnGhost} onClick={() => { if (openSite === s.id) onChanged(); setOpenSite(openSite === s.id ? null : s.id); }} data-testid={`ext-site-deliveries-${s.location_code}`}>
                  Leveringsadresser og åbningstider <ChevronDown className={`w-4 h-4 transition-transform ${openSite === s.id ? "rotate-180" : ""}`} /></button>}
                {cust.can_edit && <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700" title="Ret adresse og åbningstid" onClick={() => setEdit(s.id)} data-testid={`ext-site-edit-${s.location_code}`}><Edit2 className="w-4 h-4" /></button>}
                {cust.can_edit && <button className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600" title="Vi kører ikke længere her (jeres del fjernes - eller skjules, når der er historik)"
                  onClick={() => leaveSite(s)} data-testid={`ext-site-leave-${s.location_code}`}><LogOut className="w-4 h-4" /></button>}
              </div>
            </div>
            <SiteMaterials base={`${API}/logistics/external-customers/${cust.id}/sites/${s.id}`} canEdit={cust.can_edit} site={s}
              materialSuggestions={materialSuggestions} onChanged={onChanged} />
            <SiteNames cust={cust} site={s} names={names} onChanged={loadNames} />
            {canDelivery && <SiteFixed site={s} canEdit={cust.can_edit} />}
            {openSite === s.id && <SiteCalendars code={s.location_code} excel={{ kind: "c_calendars", companyId: cust.id }}
              siblings={cust.sites.filter((x) => x.location_code && x.id !== s.id).map((x) => ({ code: x.location_code, name: x.name }))} />}
          </div>
        ))}
        {(cust.hidden_sites || []).length > 0 && (
          <div className="text-xs text-slate-600 dark:text-slate-300 flex flex-wrap items-center gap-1.5" data-testid={`ext-hidden-sites-${cust.id}`}>
            <b>Skjulte pladser hos jer:</b>
            {cust.hidden_sites.map((h) => (
              <span key={h.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700">{h.name}
                {cust.can_edit && <button type="button" className="font-semibold underline" onClick={() => showSite(h)} data-testid={`ext-site-show-${h.location_code}`}>Vis igen</button>}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NewCustomer({ onSaved, onCancel }) {
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // 2026-10-28: Pladserne kan komme med som en liste (fx ved opstart) - én anmodning, godkendt af ContainerFlow.
  const [text, setText] = useState("");
  const [chk, setChk] = useState(null);
  const sum = sitesSummary(chk);
  const save = async () => {
    setSaving(true);
    try {
      const r = await requestExternal({ kind: "customer", customer_name: name, note, sites: parseSites(text) });
      toast.success(r.duplicate ? `${name} er allerede bedt om` : r.added ? `${r.added} pladser er lagt til jeres åbne anmodning om ${name}`
        : `Anmodning sendt: ContainerFlow opretter ${name}${sum.rows.length ? ` med ${nPl(sum.rows.length)}` : " med pladserne"} og sætter jer på som vognmand`); onSaved();
    }
    catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };
  return (
    <div className="mx-4 my-3 p-3 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/20 space-y-2" data-testid="ext-new-customer-form">
      <span className={label}>Kundens navn</span>
      <input className={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Fx ARGO" autoFocus data-testid="ext-new-customer-name" />
      <span className={label}>Pladser (valgfri - kan også bedes om bagefter)</span>
      <SitesPaste kind="customer" customerName={name} text={text} setText={setText} onChecked={setChk} />
      <span className={label}>Note til ContainerFlow (valgfri)</span>
      <textarea className={`${input} h-14`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Fx kontaktperson hos kunden" data-testid="ext-new-customer-note" />
      <p className="text-[11px] text-slate-500">ContainerFlow opretter kunden og pladserne, så hver kun findes én gang - også når flere vognmænd kører for kunden - og sætter jer på som vognmand. Derefter tilføjer I selv materialer, containere, leveringsadresser og jeres navne. En kunde uden pladser (fx et byggefirma) opretter I selv under Kunder › Egne kunder.</p>
      <OwnershipNote />
      <div className="flex justify-end gap-2">
        <button className={btnGhost} onClick={onCancel}><X className="w-4 h-4" /> Annuller</button>
        <button className={btnPrimary} disabled={saving || name.trim().length < 2 || sum.errors > 0} onClick={save} data-testid="ext-new-customer-save"><Check className="w-4 h-4" /> Send anmodning{sum.rows.length ? ` (${nPl(sum.rows.length)})` : ""}</button>
      </div>
    </div>
  );
}

/** Anmodninger til ContainerFlow - åbne og de seneste besvarede. */
function Requests({ list }) {
  if (!list.length) return null;
  const st = { open: "Afventer ContainerFlow", done: "Oprettet", rejected: "Afvist" };
  return (
    <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 text-xs space-y-1" data-testid="ext-requests">
      <b>Anmodninger</b>
      {list.map((r) => (
        <div key={r.id} className="flex flex-wrap items-center gap-2">
          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${r.status === "open" ? "bg-amber-100 text-amber-800" : r.status === "done" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{st[r.status] || r.status}</span>
          <span>{r.kind === "site" ? ((r.sites || []).length ? `${nPl(r.sites.length)} hos ${r.customer_name}` : `Plads "${r.site_name}" hos ${r.customer_name}`)
            : `Kunde "${r.customer_name}"${(r.sites || []).length ? ` med ${nPl(r.sites.length)}` : ""}`}{r.answer ? ` - ${r.answer}` : ""}</span>
        </div>
      ))}
    </div>
  );
}

/** 2026-10-28: Modtageanlæggene står på Lokationer (jeres egne steder) - kunderne under Kunder. */
export function FacilitiesSection() {
  const [facs, setFacs] = useState(null);
  const [canDelivery, setCanDelivery] = useState(false);
  const load = useCallback(() => {
    axios.get(`${API}/logistics/facilities`).then((r) => setFacs(r.data || [])).catch(() => setFacs([]));
  }, []);
  useEffect(() => {
    load();
    axios.get(`${API}/logistics/delivery-access`).then((r) => setCanDelivery(!!r.data?.can_edit)).catch(() => setCanDelivery(false));
  }, [load]);
  if (!facs) return null;
  return <Facilities list={facs} canEdit={canDelivery} onChanged={load} />;
}

export default function ExternalCustomers({ premium = false, filter = "" }) {
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);
  // 2026-10-27: Modtageanlæg og kalendere kræver rettigheden "Leveringsadresser og åbningstider".
  const [canDelivery, setCanDelivery] = useState(false);
  const [handed, setHanded] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [matSugg, setMatSugg] = useState([]);
  const [requests, setRequests] = useState([]);
  useEffect(() => {
    axios.get(`${API}/logistics/material-suggestions`).then((r) => setMatSugg(r.data?.materials || [])).catch(() => setMatSugg([]));
  }, []);
  const load = useCallback(() => {
    axios.get(`${API}/logistics/external-customers/handed-over`).then((r) => setHanded(r.data || [])).catch(() => setHanded([]));
    axios.get(`${API}/logistics/external-customers/hidden`).then((r) => setHidden(r.data || [])).catch(() => setHidden([]));
    axios.get(`${API}/logistics/external-customers`).then((r) => setList(r.data || [])).catch(() => setList([]));
    axios.get(`${API}/logistics/master-requests`).then((r) => setRequests(r.data || [])).catch(() => setRequests([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/logistics/delivery-access`).then((r) => setCanDelivery(!!r.data?.can_edit)).catch(() => setCanDelivery(false));
  }, []);
  if (!list) return null;
  if (!premium && list.length === 0) return null;
  const canEdit = list.length ? list[0].can_edit : true;
  const q = norm(filter);
  const shown = q ? list.filter((c) => norm(c.name).includes(q) || c.sites.some((x) => norm(`${x.name} ${x.location_code} ${x.city}`).includes(q))) : list;
  return (
    <>
      <section className={card} data-testid="external-customers">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-700">
          <div>
            <h2 className="font-bold text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-violet-600" /> Egne kunder med pladser</h2>
            <p className="text-xs text-slate-500">Kunder, der ikke selv bruger ContainerFlow, men har genbrugspladser med containere, I kører for (fx ARGO med Pickup). ContainerFlow opretter kunden og pladserne og sætter jer på som vognmand; I vedligeholder jeres materialer, containere, leveringsadresser, åbningstider og navne - det hele følger med, hvis kunden selv kommer på ContainerFlow.</p>
          </div>
          {premium && canEdit && !adding && <button className={btnPrimary} onClick={() => setAdding(true)} data-testid="ext-new-customer"><Plus className="w-4 h-4" /> Anmod om kunde med pladser</button>}
        </div>
        <Requests list={requests} />
        {adding && <NewCustomer onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
        {list.length === 0 && !adding && <div className="p-6 text-center text-sm text-slate-500">Ingen endnu.</div>}
        {list.length > 0 && shown.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Ingen kunder med pladser matcher "{filter}".</div>}
        {shown.map((c) => <CustomerCard key={c.id} cust={c} canDelivery={canDelivery} materialSuggestions={matSugg} onChanged={load} />)}
        {hidden.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300 flex flex-wrap items-center gap-1.5" data-testid="ext-hidden">
            <b>Skjulte:</b>
            {hidden.map((h) => (
              <span key={h.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700">{h.name}
                {canEdit && <button type="button" className="font-semibold underline" onClick={async () => {
                  try { await axios.post(`${API}/logistics/external-customers/${h.id}/show`); toast.success(`${h.name} vises igen`); load(); }
                  catch (e) { toast.error(formatApiError(e)); }
                }} data-testid={`ext-show-${h.id}`}>Vis igen</button>}
              </span>
            ))}
          </div>
        )}
        {handed.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-300" data-testid="ext-handed-over">
            <b>Bruger nu selv ContainerFlow:</b> {handed.map((h) => `${h.name}${h.until ? ` (fra ${h.until.split("-").reverse().join(".")})` : ""}`).join(", ")}.
            {" "}Pladser, kalendere og bestillinger er nu kundens - I kører videre som vognmand (Kunders genbrugspladser), og jeres priser gælder stadig.
          </div>
        )}
      </section>
    </>
  );
}
